/**
 * Hook para gerenciar sistema de notificações toast
 * Single responsibility: gerenciar state de múltiplos toasts
 */

import { useState, useCallback } from 'react';
import { ToastType } from '../components/ui/Toast';

export interface ToastMessage {
  id: string;
  type: ToastType;
  title: string;
  message?: string;
  duration?: number;
}

interface UseToastReturn {
  toasts: ToastMessage[];
  showToast: (toast: Omit<ToastMessage, 'id'>) => void;
  showSuccess: (title: string, message?: string) => void;
  showError: (title: string, message?: string) => void;
  showWarning: (title: string, message?: string) => void;
  showInfo: (title: string, message?: string) => void;
  hideToast: (id: string) => void;
  clearAllToasts: () => void;
}

/**
 * Hook customizado para gerenciar toasts de notificação
 *
 * Features:
 * - ✅ Múltiplos toasts simultâneos
 * - ✅ Auto-dismiss configurável
 * - ✅ Helpers para cada tipo (success, error, warning, info)
 * - ✅ Type-safe com TypeScript
 *
 * @returns Estado e funções para gerenciar toasts
 *
 * @example
 * const { showSuccess, showError } = useToast();
 *
 * // Mostrar toast de sucesso
 * showSuccess('Investigação criada', 'ID: 12345');
 *
 * // Mostrar toast de erro
 * showError('Erro ao expandir nó', error.message);
 */
export function useToast(): UseToastReturn {
  const [toasts, setToasts] = useState<ToastMessage[]>([]);

  /**
   * Gera ID único para o toast
   */
  const generateId = useCallback(() => {
    return `toast-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }, []);

  /**
   * Adiciona um novo toast
   */
  const showToast = useCallback(
    (toast: Omit<ToastMessage, 'id'>) => {
      const id = generateId();
      const newToast: ToastMessage = {
        id,
        ...toast,
        duration: toast.duration ?? 5000, // Default 5s
      };

      setToasts((prev) => [...prev, newToast]);

      // Auto-dismiss se duration > 0
      if (newToast.duration > 0) {
        setTimeout(() => {
          hideToast(id);
        }, newToast.duration);
      }
    },
    [generateId]
  );

  /**
   * Remove um toast específico
   */
  const hideToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  }, []);

  /**
   * Remove todos os toasts
   */
  const clearAllToasts = useCallback(() => {
    setToasts([]);
  }, []);

  /**
   * Helpers para cada tipo de toast
   */
  const showSuccess = useCallback(
    (title: string, message?: string) => {
      showToast({ type: 'success', title, message });
    },
    [showToast]
  );

  const showError = useCallback(
    (title: string, message?: string) => {
      showToast({ type: 'error', title, message, duration: 7000 }); // Erros ficam mais tempo
    },
    [showToast]
  );

  const showWarning = useCallback(
    (title: string, message?: string) => {
      showToast({ type: 'warning', title, message, duration: 6000 });
    },
    [showToast]
  );

  const showInfo = useCallback(
    (title: string, message?: string) => {
      showToast({ type: 'info', title, message });
    },
    [showToast]
  );

  return {
    toasts,
    showToast,
    showSuccess,
    showError,
    showWarning,
    showInfo,
    hideToast,
    clearAllToasts,
  };
}
